import {
	Alert,
	Badge,
	Box,
	Container,
	Divider,
	Heading,
	Inline,
	RelativeTime,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useState } from "react"
import { useOutletContext } from "react-router"
import { DryRunDialog } from "../../../../Automations/DryRunDialog"
import { JSONPreview } from "../../../../Integrations/JsonPreview"
import {
	TAutomation,
	TAutomationScheduleStart,
	TAutomationStep,
	TAutomationTemplateAutomation,
	XHR,
} from "../../../../Automations/store"
import { Helmet } from "react-helmet-async"

function formatStartType(startType: TAutomation["start_type"]) {
	return startType === "schedule" ? "Scheduled" : "Event"
}

function isScheduleStart(
	start: TAutomation["start"]
): start is TAutomationScheduleStart {
	return Boolean(start && "field" in start)
}

function describeStep(step: TAutomationStep) {
	if (step.type === "action") {
		const actionType = String(
			(step.config as { action?: { type?: string } })?.action?.type || "action"
		)

		return actionType === "create_task" ? "Create task" : "Communication"
	}

	if (step.type === "condition") {
		return "Condition block"
	}

	if (step.type === "wait") {
		return "Wait"
	}

	return step.type
}

function StepTree({
	steps,
	depth = 0,
}: {
	steps?: Array<TAutomationStep>
	depth?: number
}) {
	if (!steps?.length) {
		return <Text color="muted">No steps configured.</Text>
	}

	return (
		<Stack gap="3">
			{steps.map((step) => (
				<Box
					key={step.key}
					borderWidth="1"
					rounded="md"
					padding="4"
					marginLeft={depth ? "6" : undefined}
				>
					<Stack gap="3">
						<Inline
							justifyContent="between"
							alignItems="center"
							gap="3"
							flexWrap="wrap"
						>
							<Stack gap="1">
								<Text fontWeight="semibold">
									{step.key}{" "}
									<Text as="span" color="muted" fontWeight="normal">
										{describeStep(step)}
									</Text>
								</Text>
								<Text color="muted" fontSize="sm">
									Step {step.position}
								</Text>
							</Stack>
							<Badge
								warning={!step.is_active}
								success={step.is_active !== false}
							>
								{step.is_active === false ? "Inactive" : "Active"}
							</Badge>
						</Inline>
						<JSONPreview
							data={
								(step.config as Record<string, unknown> | undefined) ||
								undefined
							}
						/>
						{step.children?.length ? (
							<>
								<Divider />
								<Stack gap="3">
									<Text fontWeight="semibold">Nested Steps</Text>
									<StepTree steps={step.children} depth={depth + 1} />
								</Stack>
							</>
						) : null}
					</Stack>
				</Box>
			))}
		</Stack>
	)
}

export default function AutomationDetailsPage() {
	const { automation } = useOutletContext<{
		automationId: string
		automation: TAutomation
	}>()
	const xhr = useXHR()
	const [templateDetail, setTemplateDetail] =
		useState<TAutomationTemplateAutomation | null>(null)
	const [templateError, setTemplateError] = useState<string | null>(null)

	useEffect(() => {
		if (!automation.template_key) {
			setTemplateDetail(null)
			setTemplateError(null)
			return
		}

		XHR(xhr)
			.showAutomationTemplate(automation.id)
			.then((resp) => {
				setTemplateDetail(resp.data)
				setTemplateError(null)
			})
			.catch((error) => {
				setTemplateDetail(null)
				setTemplateError(
					(error as { message?: string })?.message ||
						"Template details are not available for this automation."
				)
			})
	}, [automation.id, automation.template_key, xhr])

	return (
		<>
			<Helmet>
				<title>Automation Details</title>
			</Helmet>
			<Stack gap="3">
				<Container paddingY="4">
					<Stack gap="4">
						<Inline
							justifyContent="between"
							alignItems="center"
							gap="3"
							flexWrap="wrap"
						>
							<Heading as="h3" fontSize="md">
								Definition
							</Heading>
							<DryRunDialog
								automationId={automation.id}
								sourceName={automation.source?.name || automation.source_key}
							/>
						</Inline>
						<Inline gap="6" flexWrap="wrap">
							{automation.template?.name ? (
								<Stack gap="1">
									<Text color="muted" fontSize="sm">
										Template
									</Text>
									<Text>{automation.template.name}</Text>
								</Stack>
							) : null}
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Source
								</Text>
								<Text>{automation.source?.name || automation.source_key}</Text>
							</Stack>
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Start Type
								</Text>
								<Text>{formatStartType(automation.start_type)}</Text>
							</Stack>
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Status
								</Text>
								<Badge
									success={automation.is_active}
									warning={!automation.is_active}
								>
									{automation.is_active ? "Active" : "Inactive"}
								</Badge>
							</Stack>
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Created
								</Text>
								<RelativeTime timestamp={automation.created_at} />
							</Stack>
							{automation.updated_at ? (
								<Stack gap="1">
									<Text color="muted" fontSize="sm">
										Last Updated
									</Text>
									<RelativeTime timestamp={automation.updated_at} />
								</Stack>
							) : null}
						</Inline>
						<Text color="muted" fontSize="sm">
							Dry runs create a traceable run without sending real messages or
							creating tasks.
						</Text>
					</Stack>
				</Container>
				<Divider marginY="0" />
				<Container paddingY="4">
					<Stack gap="4">
						<Heading as="h3" fontSize="md">
							Template Configuration
						</Heading>
						{templateDetail ? (
							<>
								<Inline gap="6" flexWrap="wrap">
									<Stack gap="1">
										<Text color="muted" fontSize="sm">
											Template Key
										</Text>
										<Text>{templateDetail.key}</Text>
									</Stack>
									<Stack gap="1">
										<Text color="muted" fontSize="sm">
											Parameters Source
										</Text>
										<Text>{templateDetail.parameters_source_key}</Text>
									</Stack>
								</Inline>
								<JSONPreview data={templateDetail.input || undefined} />
								<Box borderWidth="1" rounded="md" padding="4">
									<Stack gap="2">
										<Text fontWeight="semibold">Default WhatsApp Content</Text>
										{templateDetail.default_whatsapp_template.header ? (
											<Text>
												{typeof templateDetail.default_whatsapp_template
													.header === "string"
													? templateDetail.default_whatsapp_template.header
													: `Attachment: {{${templateDetail.default_whatsapp_template.header.source_key}}}`}
											</Text>
										) : null}
										<Text color="muted">
											{templateDetail.default_whatsapp_template.body}
										</Text>
									</Stack>
								</Box>
							</>
						) : templateError ? (
							<Alert title="Template details unavailable" status="warning">
								{templateError}
							</Alert>
						) : (
							<Text color="muted">
								This automation is not currently template-managed.
							</Text>
						)}
					</Stack>
				</Container>
				<Divider marginY="0" />
				<Container paddingY="4">
					<Stack gap="4">
						<Heading as="h3" fontSize="md">
							Start Configuration
						</Heading>
						{automation.start ? (
							<>
								{isScheduleStart(automation.start) ? (
									<Inline gap="6" flexWrap="wrap">
										<Stack gap="1">
											<Text color="muted" fontSize="sm">
												Schedule Field
											</Text>
											<Text>{automation.start.field}</Text>
										</Stack>
										<Stack gap="1">
											<Text color="muted" fontSize="sm">
												Checkpoints
											</Text>
											<Text>
												{automation.start.checkpoints?.length
													? automation.start.checkpoints.join(", ")
													: "None"}
											</Text>
										</Stack>
									</Inline>
								) : (
									<Inline gap="6" flexWrap="wrap">
										<Stack gap="1">
											<Text color="muted" fontSize="sm">
												Event
											</Text>
											<Text>{automation.start.event}</Text>
										</Stack>
									</Inline>
								)}
								<JSONPreview
									data={
										(automation.start as Record<string, unknown>) || undefined
									}
								/>
							</>
						) : (
							<Text color="muted">No start configuration available.</Text>
						)}
					</Stack>
				</Container>
				<Divider marginY="0" />
				<Container paddingY="4">
					<Stack gap="4">
						<Heading as="h3" fontSize="md">
							Step Flow
						</Heading>
						<StepTree steps={automation.steps} />
					</Stack>
				</Container>
			</Stack>
		</>
	)
}
